import React, { useState, useRef, useEffect } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Textarea, Select, SelectItem } from "@heroui/react";
import { useSession, useUser } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Check, X, PencilSimple } from '@phosphor-icons/react';
import { InsuranceIssue, InsuranceIssueStatus, InsuranceIssueRole } from 'shared/types/insurance';
import { toast } from 'react-toastify';

interface QuickEditInsuranceModalProps {
  isOpen: boolean;
  onClose: () => void;
  issue: InsuranceIssue | null;
  onUpdated?: (updatedIssue: InsuranceIssue) => void;
}

export const QuickEditInsuranceModal: React.FC<QuickEditInsuranceModalProps> = ({
  isOpen,
  onClose,
  issue,
  onUpdated
}) => {
  const { session } = useSession();
  const { user } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    insuranceIssueStatus: InsuranceIssueStatus.GATHERING_INFO,
    role: InsuranceIssueRole.UNKNOWN,
    actionsTaken: '',
    complexities: '',
    askedCompensation: '',
    receivedCompensation: ''
  });

  // Update form when issue changes
  useEffect(() => {
    if (issue) {
      setFormData({
        title: issue.title || '',
        description: issue.description || '',
        insuranceIssueStatus: issue.insuranceIssueStatus || InsuranceIssueStatus.GATHERING_INFO,
        role: issue.role || InsuranceIssueRole.UNKNOWN,
        actionsTaken: issue.actionsTaken || '',
        complexities: issue.complexities || '',
        askedCompensation: issue.askedCompensation || '',
        receivedCompensation: issue.receivedCompensation || ''
      });
    }
  }, [issue]);

  // Focus title input when modal opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!issue || !session) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/insurance/file/${issue.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            ...formData, 
            user: { id: user?.id } 
          })
        },
        session
      );
      
      if (!response.ok) {
        throw new Error('Update failed');
      }
      
      const updatedIssue = await response.json();
      toast.success('Dossier bijgewerkt!');
      onUpdated?.(updatedIssue);
      onClose();
    } catch (error) {
      console.error('Failed to update insurance issue:', error);
      toast.error('Fout bij bijwerken van dossier');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isSubmitting) {
      handleClose();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="3xl"
      scrollBehavior="inside"
      classNames={{
        base: "max-w-4xl",
        backdrop: "bg-black/50",
        wrapper: "items-center justify-center"
      }}
      isDismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2 pb-2">
          <PencilSimple size={24} className="text-moss-primary" />
          <span className="text-xl font-semibold text-neutral-primary">
            Bewerk dossier INS{issue?.id}
          </span>
        </ModalHeader>
        
        <ModalBody className="pb-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                ref={titleInputRef}
                label="Titel"
                placeholder="Titel van het dossier..."
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                onKeyDown={handleKeyDown}
                variant="bordered"
                classNames={{
                  input: "text-neutral-primary",
                  inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
                }}
                isDisabled={isSubmitting}
                isRequired
              />
              
              <Select
                label="Status"
                placeholder="Selecteer status"
                selectedKeys={[formData.insuranceIssueStatus]}
                onSelectionChange={(keys) => {
                  const status = Array.from(keys)[0] as InsuranceIssueStatus;
                  setFormData(prev => ({ ...prev, insuranceIssueStatus: status }));
                }}
                variant="bordered"
                classNames={{
                  trigger: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary",
                  value: "text-neutral-primary"
                }}
                isDisabled={isSubmitting}
                items={Object.values(InsuranceIssueStatus).map((status) => ({
                  key: status,
                  label: status
                }))}
              >
                {(status) => (
                  <SelectItem key={status.key}>
                    {status.label}
                  </SelectItem>
                )}
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Onze positie"
                placeholder="Selecteer rol"
                selectedKeys={[formData.role]}
                onSelectionChange={(keys) => {
                  const role = Array.from(keys)[0] as InsuranceIssueRole;
                  setFormData(prev => ({ ...prev, role }));
                }}
                variant="bordered"
                classNames={{
                  trigger: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary",
                  value: "text-neutral-primary"
                }}
                isDisabled={isSubmitting}
                items={Object.values(InsuranceIssueRole).map((role) => ({
                  key: role,
                  label: role
                }))}
              >
                {(role) => (
                  <SelectItem key={role.key}>
                    {role.label}
                  </SelectItem>
                )}
              </Select>

              <div className="flex flex-col gap-2">
                <Input
                  label="Bedrag gevraagd"
                  placeholder="€ 0,00"
                  value={formData.askedCompensation}
                  onChange={(e) => setFormData(prev => ({ ...prev, askedCompensation: e.target.value }))}
                  onKeyDown={handleKeyDown}
                  variant="bordered"
                  classNames={{
                    input: "text-neutral-primary",
                    inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
                  }}
                  isDisabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Bedrag bevestigd"
                placeholder="€ 0,00"
                value={formData.receivedCompensation}
                onChange={(e) => setFormData(prev => ({ ...prev, receivedCompensation: e.target.value }))}
                onKeyDown={handleKeyDown}
                variant="bordered"
                classNames={{
                  input: "text-neutral-primary",
                  inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
                }}
                isDisabled={isSubmitting}
              />
              <div></div>
            </div>

            <Textarea
              label="Beschrijving schadegeval"
              placeholder="Beschrijving van het schadegeval..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              onKeyDown={handleKeyDown}
              variant="bordered"
              minRows={3}
              maxRows={5}
              classNames={{
                input: "text-neutral-primary",
                inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
              }}
              isDisabled={isSubmitting}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Textarea
                label="Genomen acties"
                placeholder="Beschrijf de genomen acties..."
                value={formData.actionsTaken}
                onChange={(e) => setFormData(prev => ({ ...prev, actionsTaken: e.target.value }))}
                onKeyDown={handleKeyDown}
                variant="bordered"
                minRows={2}
                maxRows={4}
                classNames={{
                  input: "text-neutral-primary",
                  inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
                }}
                isDisabled={isSubmitting}
              />

              <Textarea
                label="Complexiteiten"
                placeholder="Beschrijf eventuele complexiteiten..."
                value={formData.complexities}
                onChange={(e) => setFormData(prev => ({ ...prev, complexities: e.target.value }))}
                onKeyDown={handleKeyDown}
                variant="bordered"
                minRows={2}
                maxRows={4}
                classNames={{
                  input: "text-neutral-primary",
                  inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
                }}
                isDisabled={isSubmitting}
              />
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-neutral-primary/10">
              <div className="text-xs text-neutral-secondary-mid">
                💡 Tip: Druk Ctrl+Enter om snel op te slaan
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="light"
                  onClick={handleClose}
                  isDisabled={isSubmitting}
                  startContent={<X size={16} />}
                >
                  Annuleren
                </Button>
                <Button
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  className="bg-moss-primary text-white hover:bg-moss-primary/90"
                  startContent={!isSubmitting ? <Check size={16} /> : undefined}
                >
                  {isSubmitting ? 'Opslaan...' : 'Wijzigingen opslaan'}
                </Button>
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QuickEditInsuranceModal;
